import {
    Icon,
    Button,
    Card,
    EmptyState,
    AutoTable,
    SelectButton,
    Modal,
    DropdownItem,
    TableCellDropdown,
    DropdownLine,
    Message,
    ToastContext,
    Toggle,
    WeezHeader
} from '@weezevent/nacre';
import React from 'react';
import Style from '../../../style/editCrossSelling.module.css';
import i18n from '../../../../../i18n/i18n';
import VariantModel from '../../../../../models/variantModel';
import VariantModal from './variantModal';
import ProductModel from '../../../../../models/productModel';
import QuotasProductModel from '../../../../../models/quotasProductModel';
import { useTranslation } from 'react-i18next';
import LoadingCard from '../../../../../components/loadingCard';
import { fetcher, useForm, requiredField } from '@weezevent/weezjs-core';
import { setTranslate } from '../../../utils/translationUtils';
import { WarningArchive } from '../../settings/formProduct';

export default function VariantTable({ organization, productId, loading, variants, syncVariants, setVariants, productLanguages }) {
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();

    const [submitting, setSubmitting] = React.useState(false);
    const [openModal, setOpenModal] = React.useState(false);
    const [openDeleteModal, setopenDeleteModal] = React.useState(false);
    const [selectedObjects, setSelectedObjects] = React.useState([]);

    const [quotas, quotasloading, syncQuota] = QuotasProductModel.useApiModel(
        {
            organizationId: organization.id,
            productId,
            allow_cache: false,
            cache: false
        },
        [organization, productId]
    );

    const [product, loadingProduct] = ProductModel.useApiModel(
        {
            organizationId: organization.id,
            id: productId,
            launch: Boolean(productId),
            cache: false,
            allow_cache: false
        },
        [productId, organization]
    );

    const shouldDisplayDisableProductMessage = React.useMemo(() => {
        return variants?.every(variant => variant.disabled);
    }, [variants]);

    const variantCanBeDeleted = React.useMemo(() => {
        return Boolean(variants?.length > 1);
    }, [variants]);

    const filteredVariants = React.useMemo(() => {
        return variants?.filter(item => selectedObjects.includes(item.id) && item.transaction_total_count < 1);
    }, [variants, selectedObjects]);

    const variantsNamesTranslations = React.useMemo(() => {
        const lang = i18n.accountLocale.toLowerCase().replace('_', '-');
        //for each variant we either use the translations if present or the name (pipetranslate)
        return variants.map(v => {
            const data = v.object || v;
            const _translations = data.translations?.name;
            if (_translations) {
                if (_translations[lang]) {
                    data.name = _translations[lang];
                    return data;
                }
                //we tolerate alternative translations similar to account language (like fr-fr for fr-ca)
                for (const trylang of Object.keys(_translations)) {
                    if (trylang.includes(lang.split('-')[0])) {
                        data.name = _translations[trylang];
                        return data;
                    }
                }
            }
            data.name = setTranslate(data.name, lang, productLanguages);
            return data;
        });
    }, [variants, i18n, setTranslate, productLanguages]);

    const sortedVariants = React.useMemo(() => {
        if (!variantsNamesTranslations) return [];

        return [...variantsNamesTranslations].sort((a, b) => {
            return a.ordering - b.ordering;
        });
    }, [variantsNamesTranslations]);

    const defaultVariant = {
        quota: null,
        name: '',
        id: null,
        price: 0,
        ordering: variants ? variants.length : 0,
        disabled: false,
        reference: '',
        translations: {
            name: {}
        }
    };

    const [form, setForm, setFormObject, formErrors, formValidate] = useForm(defaultVariant, {
        name: requiredField,
        price: requiredField
    });

    const resetModal = React.useCallback(
        (variants = null) => {
            let _defaultVariant = { ...defaultVariant };
            if (variants) {
                _defaultVariant.ordering = variants.length;
            }

            setFormObject({
                ..._defaultVariant
            });

            // reset form errors
            Object.keys(formErrors).forEach(key => {
                delete formErrors[key];
            });
        },
        [setFormObject, defaultVariant]
    );

    const setVariantForModal = React.useCallback(
        object => {
            setFormObject({
                ...form,
                quota: quotas?.find(q => q.variant === object.id)?.quota_total,
                name: object.name,
                id: object.id,
                price: object.price,
                disabled: object.visibility,
                reference: object.reference,
                ordering: object.ordering,
                translations: { ...(object.translations || form.translations) }
            });
            setOpenModal(true);
        },
        [setFormObject, quotas]
    );

    const onVariantSelected = React.useCallback(
        (obj, event) => {
            let ret = [...selectedObjects];
            if (event.target.checked) {
                ret.push(obj.id);
            } else {
                ret = ret.filter(r => obj.id !== r);
            }
            setSelectedObjects(ret);
        },
        [selectedObjects]
    );

    const handleSelectAll = React.useCallback(() => {
        if (!selectedObjects.length) {
            return setSelectedObjects(variants.filter(v => !v.disabled).map(v => v.id));
        }
        return setSelectedObjects([]);
    }, [variants, selectedObjects]);

    const handleSave = React.useCallback(
        (value, options = {}) => {
            const { id = null, newVariant = false, isDragAndDrop = false } = options;

            if (!isDragAndDrop && !formValidate()) {
                return toast.error(t('ticket.cross-selling.add.form_error'));
            }
            setSubmitting(true);

            const payload = id ? { id, ...value } : value;
            return new VariantModel(payload)
                .save({
                    organizationId: organization.id,
                    productId,
                    ...(id ? { id } : {})
                })
                .then(() => {
                    toast.success(id ? t('ticket.cross-selling.listVariant.variantEdited') : t('ticket.cross-selling.listVariant.variantAdded'));
                })
                .catch(e => {
                    console.error(e);
                    toast.error(id ? t('ticket.cross-selling.listVariant.variantEditError') : t('ticket.cross-selling.listVariant.variantSaveError'));
                })
                .finally(() => {
                    resetModal();
                    syncVariants({ allow_cache: false, cache: false });
                    syncQuota();
                    if (!newVariant) {
                        setOpenModal(false);
                    }
                    setSubmitting(false);
                });
        },
        [organization, productId, resetModal, syncVariants, syncQuota]
    );

    const handleToggleVariant = React.useCallback(
        object => {
            const index = variants.findIndex(v => v.object.id === object.id);

            const newVariants = [...variants];
            const current = newVariants[index];
            const previousDisabled = current.object.disabled;

            // Prechanging toggle value no matter what is request answer
            newVariants[index] = {
                ...current,
                object: {
                    ...current.object,
                    disabled: !current.object.disabled
                }
            };
            setVariants(newVariants);

            const updatedVariant = new VariantModel({
                id: object.id,
                disabled: !object.disabled
            });

            updatedVariant
                .save({
                    organizationId: organization.id,
                    productId,
                    id: object.id
                })
                .then(() => {
                    toast.success(t('ticket.cross-selling.listVariant.variantEdited'));
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.cross-selling.listVariant.variantEditError'));
                    // If the request fail, then we change disabled value back to it's previous state
                    setVariants(prev => {
                        const unchangedVariants = [...prev];
                        unchangedVariants[index] = {
                            ...unchangedVariants[index],
                            object: {
                                ...unchangedVariants[index].object,
                                disabled: previousDisabled
                            }
                        };
                        return unchangedVariants;
                    });
                });
        },
        [variants, quotas, organization.id, productId]
    );

    const onDelete = React.useCallback(() => {
        let variantsToDelete = filteredVariants.map(va => {
            return { ...va.object, quota: quotas?.find(q => q.variant === va.id)?.quota_total || Infinity };
        });
        setSubmitting(true);
        fetcher
            .delete(`/ticket/organizations/${organization.id}/products/${productId}/variants`, { data: variantsToDelete })
            .then(() => {
                toast.success(t('ticket.cross-selling.listVariant.variantRemoved', { count: selectedObjects?.length }));
            })
            .catch(e => {
                toast.error(t('ticket.cross-selling.listVariant.variantRemovedError', { count: selectedObjects?.length }));
            })
            .finally(() => {
                syncVariants({ allow_cache: false, cache: false });
                syncQuota();
                setSubmitting(false);
                setopenDeleteModal(false);
                setSelectedObjects([]);
            });
    }, [productId, filteredVariants, syncVariants, syncQuota, form, organization, selectedObjects]);

    const productIsArchived = React.useMemo(() => product?.archived_at != null, [product]);

    const columns = [
        {
            key: 'name',
            label: t('ticket.cross-selling.listVariant.name'),
            render: object => (
                <>
                    {object.name}
                    {object.reference ? <div className={Style['column-variant-ref']}>{`${object.reference}`}</div> : null}
                </>
            )
        },
        {
            key: 'sell',
            label: t('ticket.cross-selling.listVariant.sell'),
            render: object => object.transaction_total_count
        },
        {
            key: 'quota',
            label: t('ticket.cross-selling.listVariant.quota'),
            render: object => {
                return `${quotas?.find(q => q.variant === object.id)?.quota_total || '-'}`;
            }
        },
        {
            key: 'price',
            label: t('ticket.cross-selling.listVariant.price'),
            render: object => `${object.price} ${t(`currencies.label.${organization.currency?.toLowerCase()}`)}`
        },
        {
            key: 'disabled',
            label: t('ticket.cross-selling.listVariant.disabled'),
            render: object => (
                <div onClick={e => e.stopPropagation()}>
                    <Toggle disabled={productIsArchived} checked={!object.disabled} onChange={() => handleToggleVariant(object)} />
                </div>
            )
        },
        {
            key: 'Actions',
            label: t('ticket.cross-selling.listVariant.actions'),
            render: object => (
                <TableCellDropdown disabled={productIsArchived}>
                    <DropdownItem
                        item={t('ticket.common.action.edit')}
                        onClick={() => {
                            setVariantForModal(object);
                        }}
                    />
                    <DropdownLine />
                    <DropdownItem
                        item={t('ticket.common.action.delete')}
                        disabled={object.transaction_total_count > 0}
                        deleteItem
                        onClick={() => {
                            setSelectedObjects([object.id]);
                            setopenDeleteModal(true);
                        }}
                    />
                </TableCellDropdown>
            )
        }
    ];

    return (
        <>
            <WeezHeader
                title={t('ticket.cross-selling.listVariant.listHeader')}
                rightComponent={[
                    <Button
                        disabled={productIsArchived}
                        primary
                        key="variant"
                        onClick={() => {
                            resetModal();
                            setOpenModal(true);
                        }}
                        label={t('ticket.cross-selling.listVariant.addVariant2')}
                        icon={<Icon name="plus" margin={false} />}
                    />
                ]}
            >
                {t('ticket.cross-selling.listVariant.listSubtitleHeader')}
            </WeezHeader>
            <WarningArchive forceProduct={product} />
            <Card>
                {!variants?.length ? (
                    <Card.Content>
                        <div className={Style['empty-card-subtitle']}>
                            <EmptyState
                                icon={<Icon name="list" />}
                                title={t('ticket.cross-selling.listVariant.noVariant')}
                                labelButton={t('ticket.cross-selling.listVariant.addVariant2')}
                                onClick={() => setOpenModal(true)}
                                subtitle={<span style={{ whiteSpace: 'pre-line' }}>{t('ticket.cross-selling.listVariant.emptyStateDesc')}</span>}
                            />
                        </div>
                    </Card.Content>
                ) : (
                    <Card.Content>
                        <LoadingCard loading={loading || quotasloading || submitting}>
                            <div className={Style['add-cross-variant-container']}>
                                <SelectButton
                                    tooltipTitle={t('ticket.cross-selling.listVariant.noActionTooltip')}
                                    title={
                                        Object.values(selectedObjects).length > 0
                                            ? t('ticket.cross-selling.listVariant.variantAction', { count: Object.values(selectedObjects).length })
                                            : t('ticket.common.action.bulk')
                                    }
                                    objects={selectedObjects}
                                    disabled={productIsArchived}
                                >
                                    <DropdownItem
                                        deleteItem
                                        disabled={!variantCanBeDeleted || productIsArchived}
                                        item={t('ticket.common.action.delete')}
                                        onClick={() => {
                                            setopenDeleteModal(true);
                                        }}
                                    />
                                </SelectButton>
                            </div>
                            {shouldDisplayDisableProductMessage && (
                                <Message title={t('ticket.cross-selling.listVariant.productNotVisible')} type="warning">
                                    {t('ticket.cross-selling.listVariant.productDisable')}
                                </Message>
                            )}
                            <AutoTable
                                pk="id"
                                useNew
                                selectable={!productIsArchived}
                                objects={sortedVariants}
                                columns={columns}
                                disabled={productIsArchived}
                                selectedObjects={selectedObjects}
                                onObjectSelected={onVariantSelected}
                                onObjectClicked={setVariantForModal}
                                onSelectAll={handleSelectAll}
                                draggable={!productIsArchived}
                                onSortEnd={event => {
                                    const { oldIndex, newIndex } = event;
                                    handleSave(
                                        {
                                            id: variants[oldIndex].id,
                                            ordering: newIndex
                                        },
                                        {
                                            id: variants[oldIndex].id,
                                            isDragAndDrop: true
                                        }
                                    );
                                }}
                            />
                        </LoadingCard>
                    </Card.Content>
                )}
            </Card>

            {openModal && !productIsArchived && (
                <VariantModal
                    open={openModal}
                    setOpenModal={setOpenModal}
                    product={product}
                    quotas={quotas}
                    form={form}
                    setForm={setForm}
                    setFormObject={setFormObject}
                    formErrors={formErrors}
                    resetModal={resetModal}
                    handleSave={handleSave}
                    loading={submitting || loadingProduct}
                />
            )}

            <Modal
                size={'medium'}
                open={openDeleteModal && !productIsArchived}
                allowExternalClosing={false}
                onClose={() => setopenDeleteModal(false)}
                title={t('ticket.cross-selling.listVariant.confirmDeleteVariant', { count: selectedObjects.length })}
                submitButton={<Button primary onClick={() => onDelete()} label={t('ticket.common.action.confirm')} />}
                cancelButton={<Button onClick={() => setopenDeleteModal(false)} label={t('ticket.common.action.cancel')} />}
            >
                {selectedObjects.length > filteredVariants.length && (
                    <Message type="default" title={t('ticket.cross-selling.listVariant.confirmDeleteProductVariant_message_title_filtered')}>
                        {t('ticket.cross-selling.listVariant.confirmDeleteProductVariant_message_body_filtered')}
                    </Message>
                )}
                {t('ticket.cross-selling.listVariant.confirmDeleteProductVariantQuestion', { count: selectedObjects.length })}
            </Modal>
        </>
    );
}
